import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Col, DatePicker, Form, FormInstance, Input, InputNumber, message, Row, Select, TimePicker } from 'antd';
import { apiClient, apiErrorMessage, getDataTypeName } from '../../../utils';
import { Field, Grid, GridRow, Integration, Permission, Role, User, UserRole } from '../../../models';
import { DeleteOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { FormListFieldData } from 'antd/lib/form/FormList';

export function RolePermissionRulesPage(props: { role?: Role; permission?: Permission; onSaved: (role: Role) => void }) {
  const [loading, setLoading] = useState(false);
  const [grids, setGrids] = useState<Grid[]>();
  const [grid, setGrid] = useState<Grid>();
  const [gridField, setGridField] = useState<Field>();
  const [form] = Form.useForm();
  const [roleUsers, setRoleUsers] = useState<User[]>();

  // TODO - if the integration is EXPORT, this modal should be avoided in the first place.
  const [notAvailable, setNotAvailable] = useState(false);

  const permissionTypeRef = useRef<string>();

  useEffect(() => {
    apiClient.get(`grids`).then((response: any) => {
      const responseGrids = response.data as Grid[];
      setGrids(responseGrids);
    });
  }, []);

  useEffect(() => {
    if (!grids) {
      return;
    }

    const key = props.permission?.key;
    const keyArray = key?.split('.');
    if (!keyArray) {
      return;
    }

    permissionTypeRef.current = keyArray[0];

    if (permissionTypeRef.current === 'grids' && keyArray.length < 3) {
      return;
    }
    if (permissionTypeRef.current === 'integrations' && keyArray.length < 3) {
      return;
    }

    if (permissionTypeRef.current === 'grids') {
      const gridId = keyArray[1];
      const fieldId = keyArray[3];

      const responseGrid = grids?.find(g => g.id === gridId);
      setGrid(responseGrid);
      setGridField(responseGrid?.fields.find(f => f.id === fieldId));
    } else {
      const integrationId = keyArray[1];

      apiClient
        .get(`integrations/${integrationId}`)
        .then((response: any) => {
          const responseIntegration = response.data as Integration;
          if (responseIntegration.integrationType !== 'EXPORT') {
            setNotAvailable(true);
          } else {
            apiClient
              .get(`grids/${responseIntegration.primaryGridId}`)
              .then((response: any) => {
                const responseGrid = response.data as Grid;
                setGrid(responseGrid);
              })
              .catch((reason: any) => {
                apiErrorMessage(reason);
              })
              .finally(() => {
                setLoading(false);
              });
          }
        })
        .catch((reason: any) => {
          apiErrorMessage(reason);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [grids, props.permission]);

  useEffect(() => {
    apiClient
      .get(`roles/${props.role?.id}/users`)
      .then((response: any) => {
        const userRoles = response.data as UserRole[];
        setRoleUsers(userRoles?.filter(ur => !!ur.user).map(ur => ur.user));
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  }, [props.role?.id]);

  useEffect(() => {
    const rolePermission = props.role?.permissions.find(p => p.key === props.permission?.key);
    if (!rolePermission?.rule?.length) {
      return;
    }
    const rule = rolePermission.rule.map(r => {
      return { ruleItem: r };
    });
    form.setFieldsValue({ rule });
  }, [form, props.permission, props.role]);

  const onFinish = (values: any) => {
    const rule = values.rule as any[];
    const ruleItems = rule.map(r => r.ruleItem);
    const body = { permissionKey: props.permission?.key, rule: ruleItems };
    setLoading(true);
    apiClient
      .put(`roles/${props.role?.id}/permission-rules`, body)
      .then((response: any) => {
        message.success('Yetki kuralları başarıyla kaydedildi.');
        props.onSaved(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      {notAvailable ? (
        <span>Kurallar sadece dışa entegrasyonlar için belirlenebilir.</span>
      ) : (
        <Form name="basic" form={form} initialValues={{ rule: [{ ruleItem: [''] }] }} onFinish={onFinish}>
          <Form.List name="rule">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <RuleFormItem
                    form={form}
                    field={field}
                    index={index}
                    remove={remove}
                    key={field.key}
                    grids={grids}
                    grid={grid}
                    gridField={gridField}
                    roleUsers={roleUsers}
                  />
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                    Kural ekle
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Kaydet
            </Button>
          </Form.Item>
        </Form>
      )}
    </>
  );
}

function RuleFormItem(props: {
  form: FormInstance<any>;
  field: FormListFieldData;
  index: number;
  remove: (index: number | number[]) => void;
  grids?: Grid[];
  grid?: Grid;
  gridField?: Field;
  roleUsers?: User[];
}) {
  return (
    <Form.List name={[props.field.name, 'ruleItem']}>
      {(fields, { add, remove }) => (
        <Row>
          <Col span={23}>
            {fields.map((field, index) => (
              <RuleFormItemItem
                form={props.form}
                field={field}
                parentIndex={props.index}
                index={index}
                remove={remove}
                key={field.key}
                gridFields={props.grid?.fields}
                gridField={props.gridField}
                roleUsers={props.roleUsers}
                grids={props.grids}
              />
            ))}
            <Form.Item>
              <Button type="link" onClick={() => add()}>
                Koşul ekle
              </Button>
            </Form.Item>
          </Col>
          <Col span={1} style={{ textAlign: 'right' }}>
            <DeleteOutlined style={{ marginTop: '10px' }} onClick={() => props.remove(props.field.name)} />
          </Col>
        </Row>
      )}
    </Form.List>
  );
}

function RuleFormItemItem(props: {
  form: FormInstance<any>;
  field: FormListFieldData;
  parentIndex: number;
  index: number;
  remove: (index: number | number[]) => void;
  gridFields?: Field[];
  gridField?: Field;
  roleUsers?: User[];
  grids?: Grid[];
}) {
  const [selectedFieldId, setSelectedFieldId] = useState<string>();
  const [selectedField, setSelectedField] = useState<Field>();
  const [ops, setOps] = useState<any[]>();
  const [selectedOp, setSelectedOp] = useState<string>();
  const [rows, setRows] = useState<GridRow[]>();
  const [rowsLoading, setRowsLoading] = useState(false);

  const getOperatorsList = useCallback((dataType: string | undefined) => {
    if (!dataType) {
      return undefined;
    }

    const equalOps = [
      'BOOLEAN',
      'DATE',
      'DOUBLE',
      'INTEGER',
      'PICKLIST',
      'MULTIPICKLIST',
      'STRING',
      'TEXT',
      'DATETIME',
      'TIME',
      'DECIMAL',
      'URL',
      'FOREIGNKEY',
      'MULTIFOREIGNKEY',
      'DYNAMIC',
      'RULEBASED',
      'LOOKUP',
    ];
    const greaterOps = ['DATE', 'DOUBLE', 'INTEGER', 'DATETIME', 'TIME', 'DECIMAL'];
    const inOps = ['DOUBLE', 'INTEGER', 'STRING', 'DECIMAL', 'URL', 'FOREIGNKEY', 'PICKLIST', 'DYNAMIC', 'RULEBASED', 'LOOKUP'];
    const likeOps = [
      'PICKLIST',
      'MULTIPICKLIST',
      'STRING',
      'TEXT',
      'URL',
      'FOREIGNKEY',
      'MULTIFOREIGNKEY',
      'DYNAMIC',
      'RULEBASED',
      'LOOKUP',
    ];

    const operators = [];

    if (equalOps.some(m => m === dataType)) {
      operators.push({ value: '==', name: 'eşit' }, { value: '!=', name: 'eşit değil' });
    }
    if (greaterOps.some(m => m === dataType)) {
      operators.push(
        { value: '>', name: 'büyük' },
        { value: '>=', name: 'büyük eşit' },
        { value: '<', name: 'küçük' },
        { value: '<=', name: 'küçük eşit' },
      );
    }
    if (inOps.some(m => m === dataType)) {
      operators.push({ value: 'in', name: 'içinde' }, { value: '!in', name: 'içinde değil' });
    }
    if (likeOps.some(m => m === dataType)) {
      operators.push({ value: 'startsWith', name: 'başlar' }, { value: 'endsWith', name: 'biter' }, { value: 'contains', name: 'içerir' });
    }
    operators.push({ value: 'null', name: 'boş' }, { value: '!null', name: 'boş değil' });

    return operators;
  }, []);

  // TODO - Temporarily uses rows endpoint. Specialized endpoint for foreign key rows is still in progress.
  const loadForeignKeyRows = useCallback((gridId: string) => {
    if (!gridId) {
      return;
    }
    setRows(undefined);
    setRowsLoading(true);

    const body = {
      gridId,
      limit: 100,
      offset: 0,
      conditions: {
        logic: 'AND',
        items: [
          {
            objectType: 'record',
            objectId: 'MDM_RECORD',
            operator: 'activeStatus',
            value: 'active',
          },
        ],
      },
    };

    apiClient
      .post(`records/records/query`, body)
      .then((response: any) => {
        setRows(response.data.records as GridRow[]);
      })
      .catch((reason: any) => {})
      .finally(() => setRowsLoading(false));
  }, []);

  useEffect(() => {
    const { rule } = props.form.getFieldsValue();
    if (!rule?.length || !rule[props.parentIndex]?.ruleItem?.length) {
      return;
    }
    const ruleItem = rule[props.parentIndex].ruleItem[props.index];
    if (!ruleItem) {
      return;
    }
    setSelectedFieldId(ruleItem.fieldId);
    if (ruleItem.fieldId !== 'users') {
      const field = props.gridFields?.find(f => f.id === ruleItem.fieldId);
      setSelectedField(field);
      setSelectedOp(ruleItem.op);
      setOps(getOperatorsList(field?.dataType));

      if (field?.dataType === 'FOREIGNKEY' || field?.dataType === 'MULTIFOREIGNKEY') {
        loadForeignKeyRows(field.foreignKeyGridId);
      }
    } else {
      setSelectedField(undefined);
      setSelectedOp(undefined);
      setOps(undefined);
    }
  }, [getOperatorsList, loadForeignKeyRows, props.form, props.gridFields, props.index, props.parentIndex]);

  const onFieldChange = (fieldId: string) => {
    const { rule } = props.form.getFieldsValue();
    if (rule && rule.length >= props.parentIndex + 1) {
      const ruleItem = rule[props.parentIndex].ruleItem;
      if (ruleItem && ruleItem.length >= props.index + 1) {
        delete rule[props.parentIndex].ruleItem[props.index].value;
        delete rule[props.parentIndex].ruleItem[props.index].op;
        setSelectedOp(undefined);
      }
      props.form.setFieldsValue({ rule });
    }
    const field = props.gridFields?.find(gf => gf.id === fieldId);
    setSelectedFieldId(fieldId);
    setSelectedField(field);
    setOps(getOperatorsList(field?.dataType));

    if (field?.dataType === 'FOREIGNKEY' || field?.dataType === 'MULTIFOREIGNKEY') {
      loadForeignKeyRows(field.foreignKeyGridId);
    }
  };

  const onOpChange = (op: string) => {
    const { rule } = props.form.getFieldsValue();
    if (rule && rule.length >= props.parentIndex + 1) {
      const ruleItem = rule[props.parentIndex].ruleItem;
      if (ruleItem && ruleItem.length >= props.index + 1) {
        delete rule[props.parentIndex].ruleItem[props.index].value;
      }
      props.form.setFieldsValue({ rule });
    }
    setSelectedOp(op);
  };

  return (
    <Row align="middle" style={{ marginBottom: '0.5rem' }}>
      <Col span={9} style={{ paddingRight: '0.5rem' }}>
        <Form.Item
          {...props.field}
          name={[props.field.name, 'fieldId']}
          style={{ marginBottom: 0 }}
          rules={[{ required: true, message: 'Zorunlu alan' }]}
        >
          <Select
            placeholder="Veri elemanı"
            showSearch
            filterOption={(input, option) => option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
            onChange={onFieldChange}
          >
            {props.gridFields
              ?.sort((a, b) => (a.name < b.name ? -1 : 1))
              .filter(gf => gf.id !== props.gridField?.id)
              .map(gf => (
                <Select.Option key={gf.id} value={gf.id}>
                  {`${gf.name} (${getDataTypeName(gf.dataType)})`}
                </Select.Option>
              ))}
            <Select.Option key="users" value="users">
              Kullanıcılar
            </Select.Option>
          </Select>
        </Form.Item>
      </Col>
      {selectedFieldId !== 'users' && (
        <Col span={5} style={{ paddingRight: '0.5rem' }}>
          <Form.Item
            {...props.field}
            name={[props.field.name, 'op']}
            style={{ marginBottom: 0 }}
            rules={[{ required: true, message: 'Zorunlu alan' }]}
          >
            <Select placeholder="İşlem" disabled={!ops} onChange={onOpChange}>
              {ops?.map(o => (
                <Select.Option value={o.value} key={o.value}>
                  {o.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      )}
      {selectedFieldId !== 'users' ? (
        <Col span={9}>
          {selectedField?.dataType && selectedOp !== 'null' && selectedOp !== '!null' && (
            <>
              {(selectedField?.dataType === 'STRING' ||
                selectedField?.dataType === 'TEXT' ||
                selectedField?.dataType === 'URL' ||
                selectedField?.dataType === 'DYNAMIC' ||
                selectedField?.dataType === 'RULEBASED' ||
                selectedField?.dataType === 'LOOKUP') && (
                <>
                  {(selectedOp === '==' ||
                    selectedOp === '!=' ||
                    selectedOp === 'startsWith' ||
                    selectedOp === 'endsWith' ||
                    selectedOp === 'contains') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'value']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Input placeholder="Değer" disabled={!ops} />
                    </Form.Item>
                  )}
                  {(selectedOp === 'in' || selectedOp === '!in') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'value']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select mode="tags" notFoundContent={null} placeholder="Değer" />
                    </Form.Item>
                  )}
                </>
              )}

              {selectedField?.dataType === 'INTEGER' && (
                <>
                  {(selectedOp === '==' ||
                    selectedOp === '!=' ||
                    selectedOp === '>' ||
                    selectedOp === '>=' ||
                    selectedOp === '<' ||
                    selectedOp === '<=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'value']}
                      style={{ marginBottom: 0 }}
                      rules={[
                        { required: true, message: 'Zorunlu alan' },
                        { type: 'integer', message: 'Geçersiz tam sayı' },
                      ]}
                    >
                      <InputNumber placeholder="Değer" style={{ width: '100%' }} disabled={!ops} />
                    </Form.Item>
                  )}
                  {(selectedOp === 'in' || selectedOp === '!in') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'value']}
                      style={{ marginBottom: 0 }}
                      rules={[
                        { required: true, message: 'Zorunlu alan' },
                        {
                          validator: async (_, values) => {
                            if (!values || !values.length) {
                              return;
                            }
                            for (let i = 0; i < values.length; i++) {
                              if (!Number.isInteger(parseInt(values[i]))) {
                                return Promise.reject(new Error('Geçersiz tam sayı'));
                              }
                            }
                          },
                        },
                      ]}
                    >
                      <Select mode="tags" notFoundContent={null} placeholder="Değer" />
                    </Form.Item>
                  )}
                </>
              )}
              {(selectedField?.dataType === 'DOUBLE' || selectedField?.dataType === 'DECIMAL') && (
                <>
                  {(selectedOp === '==' ||
                    selectedOp === '!=' ||
                    selectedOp === '>' ||
                    selectedOp === '>=' ||
                    selectedOp === '<' ||
                    selectedOp === '<=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'value']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <InputNumber placeholder="Değer" style={{ width: '100%' }} disabled={!ops} />
                    </Form.Item>
                  )}
                  {(selectedOp === 'in' || selectedOp === '!in') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'value']}
                      style={{ marginBottom: 0 }}
                      rules={[
                        { required: true, message: 'Zorunlu alan' },
                        {
                          validator: async (_, values) => {
                            if (!values || !values.length) {
                              return;
                            }
                            for (let i = 0; i < values.length; i++) {
                              const isNumeric =
                                (typeof values[i] === 'number' || (typeof values[i] === 'string' && values[i].trim() !== '')) &&
                                !isNaN(values[i] as number);
                              if (!isNumeric) {
                                return Promise.reject(new Error('Geçersiz sayı'));
                              }
                            }
                          },
                        },
                      ]}
                    >
                      <Select mode="tags" notFoundContent={null} placeholder="Değer" />
                    </Form.Item>
                  )}
                </>
              )}
              {(selectedField?.dataType === 'DATE' || selectedField?.dataType === 'DATETIME') && (
                <>
                  {(selectedOp === '==' ||
                    selectedOp === '!=' ||
                    selectedOp === '>' ||
                    selectedOp === '>=' ||
                    selectedOp === '<' ||
                    selectedOp === '<=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'value']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <DatePicker
                        placeholder="Değer"
                        showTime={selectedField?.dataType === 'DATETIME'}
                        showSecond={false}
                        style={{ width: '100%' }}
                        disabled={!ops}
                      />
                    </Form.Item>
                  )}
                </>
              )}
              {selectedField?.dataType === 'TIME' && (
                <>
                  {(selectedOp === '==' ||
                    selectedOp === '!=' ||
                    selectedOp === '>' ||
                    selectedOp === '>=' ||
                    selectedOp === '<' ||
                    selectedOp === '<=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'value']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <TimePicker placeholder="Değer" style={{ width: '100%' }} disabled={!ops} />
                    </Form.Item>
                  )}
                </>
              )}
              {selectedField?.dataType === 'BOOLEAN' && (
                <>
                  {(selectedOp === '==' || selectedOp === '!=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'value']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select>
                        <Select.Option value={'###true###'}>Evet</Select.Option>
                        <Select.Option value={'###false###'}>Hayır</Select.Option>
                      </Select>
                    </Form.Item>
                  )}
                </>
              )}
              {selectedField?.dataType === 'PICKLIST' && (
                <>
                  {(selectedOp === '==' || selectedOp === '!=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'value']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select>
                        {selectedField.picklistValues?.map(p => (
                          <Select.Option key={p.id} value={p.id}>
                            {p.val}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  {(selectedOp === 'in' || selectedOp === '!in') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'value']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select mode="multiple">
                        {selectedField.picklistValues?.map(p => (
                          <Select.Option key={p.id} value={p.id}>
                            {p.val}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  {(selectedOp === 'startsWith' || selectedOp === 'endsWith' || selectedOp === 'contains') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Input placeholder="Değer" disabled={!ops} />
                    </Form.Item>
                  )}
                </>
              )}
              {selectedField?.dataType === 'MULTIPICKLIST' && (
                <>
                  {(selectedOp === '==' || selectedOp === '!=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'value']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select mode="multiple">
                        {selectedField.picklistValues?.map(p => (
                          <Select.Option key={p.val} value={p.val}>
                            {p.val}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  {(selectedOp === 'startsWith' || selectedOp === 'endsWith' || selectedOp === 'contains') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'value']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Input placeholder="Değer" disabled={!ops} />
                    </Form.Item>
                  )}
                </>
              )}
              {selectedField?.dataType === 'FOREIGNKEY' && (
                <>
                  {(selectedOp === '==' || selectedOp === '!=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'value']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select loading={rowsLoading}>
                        {rows?.map(r => {
                          const grid = props.grids?.find(g => g.id === r.gridId);
                          const fieldIds = grid?.fields.filter(m => m.isIdentifier).map(m => m.id);
                          const values: string[] = [];
                          fieldIds?.forEach(id => {
                            const cell = r.cells[id];
                            if (cell.value) {
                              values.push(cell.value.toString());
                            }
                          });
                          if (!values.length) {
                            return null;
                          }
                          return (
                            <Select.Option key={r.id} value={r.id}>
                              {values.join(' - ')}
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  )}
                  {(selectedOp === 'in' || selectedOp === '!in') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'value']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select mode="multiple" loading={rowsLoading}>
                        {rows?.map(r => {
                          const grid = props.grids?.find(g => g.id === r.gridId);
                          const fieldIds = grid?.fields.filter(m => m.isIdentifier).map(m => m.id);
                          const values: string[] = [];
                          fieldIds?.forEach(id => {
                            const cell = r.cells[id];
                            if (cell.value) {
                              values.push(cell.value.toString());
                            }
                          });
                          if (!values.length) {
                            return null;
                          }
                          return (
                            <Select.Option key={r.id} value={r.id}>
                              {values.join(' - ')}
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  )}
                  {(selectedOp === 'startsWith' || selectedOp === 'endsWith' || selectedOp === 'contains') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Input placeholder="Değer" disabled={!ops} />
                    </Form.Item>
                  )}
                </>
              )}
              {selectedField?.dataType === 'MULTIFOREIGNKEY' && (
                <>
                  {(selectedOp === '==' || selectedOp === '!=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'value']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select mode="multiple" loading={rowsLoading}>
                        {rows?.map(r => {
                          const grid = props.grids?.find(g => g.id === r.gridId);
                          const fieldIds = grid?.fields.filter(m => m.isIdentifier).map(m => m.id);
                          const values: string[] = [];
                          fieldIds?.forEach(id => {
                            const cell = r.cells[id];
                            if (cell.value) {
                              values.push(cell.value.toString());
                            }
                          });
                          if (!values.length) {
                            return null;
                          }
                          return (
                            <Select.Option key={r.id} value={r.id}>
                              {values.join(' - ')}
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  )}
                  {(selectedOp === 'startsWith' || selectedOp === 'endsWith' || selectedOp === 'contains') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'value']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Input placeholder="Değer" disabled={!ops} />
                    </Form.Item>
                  )}
                </>
              )}
            </>
          )}
        </Col>
      ) : (
        <Col span={14}>
          <Form.Item
            {...props.field}
            name={[props.field.name, 'value']}
            style={{ marginBottom: 0 }}
            rules={[{ required: true, message: 'Zorunlu alan' }]}
          >
            <Select
              mode="multiple"
              showSearch
              filterOption={(input, option) => option?.children?.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0}
              style={{ width: '100%' }}
            >
              {props.roleUsers?.map(u => (
                <Select.Option key={u.id} value={u.id}>
                  {u.fullName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      )}
      <Col span={1} style={{ textAlign: 'right' }}>
        <MinusCircleOutlined style={{ marginTop: '8px' }} onClick={() => props.remove(props.field.name)} />
      </Col>
    </Row>
  );
}
